/*
This file is located in the main branch
Last time the file was updated (UTC): [11/6/2024 4:40:59AM]
Permissions to update outside the main branch: [false]
*/

import {
    ColumnType,
    getFilterElementsDefault,
    useWorkplaceState,
    WorkplaceBtnActions,
    WorkplaceBtnDelete,
} from '@finbeat/react-toolkit';
import { faBoltLightning, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    addDictionaryItem,
    changeDictionaryItem,
    deleteDictionaryItem,
    getDictionary,
    getDictionaryExcel,
    getDictionaryItem,
} from 'axios/dictionaryApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import {
    DictionaryAdd,
    DictionaryChange,
    DictionaryFilter as DictionaryFilterType,
    DictionaryTable,
} from 'models/api/dictionary';
import DictItem from 'models/api/dictItem';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { useCallback, useState } from 'react';
import { FormatterProps } from 'react-data-grid';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { checkboxFormatter } from 'utils/columnFormatters';
import DictionaryAddButton from './DictionaryAddButton';
import DictionaryChangeModal from './DictionaryChangeModal';
import DictionaryFilter from './DictionaryFilter';

type DictionaryFormatterProps = FormatterProps<DictionaryTable, DictionaryTable[]>;

const Dictionary = () => {
    const { t, i18n } = useTranslation();
    const { cultures } = useGeneralSettingsContext();
    const api = useApi();
    const queryClient = useQueryClient();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const { hasRole } = useAuth();

    const culturesFormatter = useCallback((i: DictItem) => {
        const component = ({ row: { translations } }: DictionaryFormatterProps) => (
            <>{translations[i.code]}</>
        );
        return component;
    }, []);

    const actionsFormatter = useCallback(
        ({ row: { id, isUserEditable } }: DictionaryFormatterProps) => (
            <WorkplaceBtnActions>
                {hasRole('dictionaryDelete') && isUserEditable && (
                    <WorkplaceBtnDelete key={`delete-btn-${id}`} id={id} />
                )}
            </WorkplaceBtnActions>
        ),
        []
    );

    return (
        <BOWorkplace<
            DictionaryTable,
            DictionaryTable,
            DictionaryFilterType,
            DictionaryChange,
            DictionaryAdd
        >
            testId="dictionary"
            pageTitle="BackOfficeDicts"
            filterParams={{
                dictCode: 'ProductCategory',
                dictName: '[ProductCategory]: Категории товаров',
            }}
            initColumns={[
                {
                    key: 'id',
                    name: <Trans i18nKey="BackOfficeId" />,
                },
                { key: 'code', name: <Trans i18nKey="BackOfficeCode" /> },
                {
                    key: 'name',
                    name: <Trans i18nKey="BackOfficeName" />,
                    draggable: false,
                },
                { key: 'sorting', name: <Trans i18nKey="BackOfficeSorting" /> },
                ...cultures.map<ColumnType<DictionaryTable, DictionaryTable[]>>((x) => ({
                    key: x.code,
                    name: x.name,
                    formatter: culturesFormatter(x),
                })),
                { key: 'dsc', name: <Trans i18nKey="BackOfficeDescription" /> },
                {
                    key: 'notUsed',
                    name: <FontAwesomeIcon icon={faEyeSlash} />,
                    cellClass: 'cell-centered',
                    headerCellClass: 'cell-centered',
                    width: 70,
                    minWidth: 70,
                    resizable: false,
                    notHidden: true,
                    editable: true,
                    formatter: checkboxFormatter('notUsed'),
                },
                {
                    key: 'actions',
                    name: <FontAwesomeIcon icon={faBoltLightning} />,
                    cellClass: 'cell-centered no-change-modal',
                    headerCellClass: 'cell-centered',
                    width: 70,
                    minWidth: 70,
                    resizable: false,
                    clickDisable: true,
                    formatter: actionsFormatter,
                },
            ]}
            storagePath="dictionary/columns"
            getFilterElements={getFilterElementsDefault([
                { id: 'dictName', name: t('BackOfficeDict'), required: true },
            ])}
            showModal={hasRole('dictionaryGetById') ? () => setIsModalVisible(true) : undefined}
            filter={<DictionaryFilter />}
            modal={{
                component: isModalVisible && (
                    <DictionaryChangeModal isOpen={isModalVisible} toggle={setIsModalVisible} />
                ),
                addMutationFn: (item) => addDictionaryItem(api, item),
                changeMutationFn: (item) => changeDictionaryItem(api, item),
                fetchCurrentItemQueryFn: (item) => getDictionaryItem(api, item),
            }}
            fetchQueryFn={(filter) => getDictionary(api, filter)}
            buttons={
                hasRole('dictionaryCreate')
                    ? [<DictionaryAddButton key="1" showModal={() => setIsModalVisible(true)} />]
                    : []
            }
            deleteMutationFn={(id) => deleteDictionaryItem(api, id)}
            queryKey={['dictionary', i18n.language]}
            queryClient={queryClient}
            fetchExcelQueryFn={(filter) => getDictionaryExcel(api, filter)}
            showExcelBtn={hasRole('dictionaryExcel')}
        />
    );
};

const useDictionaryWorkplaceState = () =>
    useWorkplaceState<DictionaryTable, DictionaryTable, DictionaryChange, DictionaryAdd>();

export default Dictionary;
export { useDictionaryWorkplaceState };
